import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { Input } from '../ui/Input';
import { Checkbox } from '../ui/Checkbox';
import { Button } from '../ui/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '../ui/Dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '../ui/AlertDialog';
import PanelWithClassification from '../PanelWithClassification';
import ActionList from '../elements/ActionList';
import type { PickerItem, PickerSubmitResult } from './classificationPicker';
import { fetchPickerPages } from './fetchPickerPages';

interface Group {
  id: string | number;
  title: string;
}
interface Selection {
  item: PickerItem;
  group: Group;
}
type SubmitReturn = void | PickerSubmitResult | Promise<void | PickerSubmitResult>;
interface BaseProps {
  visible?: boolean;
  onClose?: () => void;
  url?: string;
  pickerUrl?: string;
  showDetail?: boolean;
  alreadySelectedValues?: PickerItem[];
  sessionKey?: string;
}
type Props = BaseProps &
  (
    | { multiple: true; onSubmit?: (items: PickerItem[]) => SubmitReturn }
    | { multiple?: false; onSubmit?: (item: PickerItem) => SubmitReturn }
    | { multiple: boolean; onSubmit?: (item: any) => SubmitReturn }
  );

// A new target/category/opening gets a completely independent draft and request lifetime.
export default function PickValueWithClassificationModal(props: Props = {}) {
  if (!props.visible) return null;
  return (
    <PickerSession key={`${props.sessionKey || ''}:${props.url}:${props.pickerUrl}`} {...props} />
  );
}

function PickerSession(props: Props) {
  const {
    onClose,
    url,
    pickerUrl,
    multiple = false,
    showDetail = false,
    alreadySelectedValues = [],
  } = props;
  const [group, setGroup] = useState<Group | null>(null);
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<PickerItem[]>([]);
  const [selection, setSelection] = useState<Selection[]>([]);
  const [undo, setUndo] = useState<Selection[] | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState<PickerItem | null>(null);
  const [discard, setDiscard] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [retry, setRetry] = useState(0);
  const busy = useRef(false);
  const alive = useRef(true);
  const anchor = useRef<string | number | null>(null);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    anchor.current = null;
    const controller = new AbortController();
    setItems([]);
    setLoadError('');
    if (!group) return () => controller.abort();
    setLoading(true);
    const timeout = setTimeout(
      () => {
        fetchPickerPages<PickerItem>(
          `/api/v1/${pickerUrl || url}`,
          {
            classification: group.id,
            pagination: false,
            search: search || undefined,
          },
          controller.signal
        )
          .then((rows) => {
            if (!controller.signal.aborted)
              setItems([...new Map(rows.map((item) => [String(item.id), item])).values()]);
          })
          .catch(() => {
            if (!controller.signal.aborted) setLoadError('Položky se nepodařilo načíst.');
          })
          .finally(() => {
            if (!controller.signal.aborted) setLoading(false);
          });
      },
      search ? 300 : 0
    );
    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [group?.id, search, pickerUrl, url, retry]);

  const existing = new Set(alreadySelectedValues.filter(Boolean).map((item) => String(item.id)));
  const current = selection.filter(({ item }) => !existing.has(String(item.id)));
  const selectedIds = new Set(current.map(({ item }) => String(item.id)));
  const visibleItems = multiple ? items : items.filter((item) => !existing.has(String(item.id)));
  const available = items.filter((item) => !existing.has(String(item.id)));
  const selectedVisible = available.filter((item) => selectedIds.has(String(item.id))).length;
  const allChecked = available.length > 0 && selectedVisible === available.length;
  const grouped = new Map<string, { group: Group; rows: Selection[] }>();
  current.forEach((row) => {
    const key = String(row.group.id);
    if (!grouped.has(key)) grouped.set(key, { group: row.group, rows: [] });
    grouped.get(key)!.rows.push(row);
  });
  const hiddenCount =
    search && !loading && !loadError
      ? current.filter(
          (row) =>
            row.group.id === group?.id &&
            !items.some((item) => String(item.id) === String(row.item.id))
        ).length
      : 0;
  const change = (next: Selection[]) => {
    setSelection(next);
    setUndo(null);
    setSubmitError('');
  };
  const remove = (id: string | number) =>
    change(current.filter((row) => String(row.item.id) !== String(id)));
  const selectRows = (rows: PickerItem[]) => {
    if (!group || busy.current) return;
    const next = new Map(current.map((row) => [String(row.item.id), row]));
    rows
      .filter((item) => !existing.has(String(item.id)))
      .forEach((item) => {
        if (!next.has(String(item.id))) next.set(String(item.id), { item, group });
      });
    change([...next.values()]);
  };
  const toggleAll = () => {
    if (allChecked) {
      const ids = new Set(available.map((item) => String(item.id)));
      change(current.filter((row) => !ids.has(String(row.item.id))));
    } else selectRows(available);
  };
  const toggle = (item: PickerItem, event?: React.MouseEvent) => {
    if (!group || busy.current || existing.has(String(item.id))) return;
    if (!multiple) {
      change([{ item, group }]);
      return;
    }
    const start = items.findIndex((row) => row.id === anchor.current);
    const end = items.findIndex((row) => row.id === item.id);
    if (event?.shiftKey && start >= 0 && end >= 0)
      selectRows(items.slice(Math.min(start, end), Math.max(start, end) + 1));
    else if (selectedIds.has(String(item.id))) remove(item.id);
    else selectRows([item]);
    anchor.current = item.id;
  };
  const close = () => {
    if (busy.current) return;
    if (multiple && current.length) setDiscard(true);
    else onClose?.();
  };
  const submit = async () => {
    if (busy.current || !current.length || !props.onSubmit) return;
    busy.current = true;
    setSubmitting(true);
    setSubmitError('');
    try {
      const result =
        props.multiple === true
          ? await props.onSubmit(current.map((row) => row.item))
          : await (props.onSubmit as (item: PickerItem) => SubmitReturn)(current[0].item);
      if (!alive.current) return;
      if (result && result.error) {
        const completed = new Set((result.completedIds || []).map(String));
        setSelection(current.filter((row) => !completed.has(String(row.item.id))));
        setUndo(null);
        setSubmitError(result.error);
      } else {
        setSelection([]);
        onClose?.();
      }
    } catch {
      if (alive.current)
        setSubmitError('Položky se nepodařilo přidat. Výběr zůstal zachován, zkuste to znovu.');
    } finally {
      busy.current = false;
      if (alive.current) setSubmitting(false);
    }
  };
  const removeButton = (row: Selection) => (
    <button
      type="button"
      disabled={submitting}
      aria-label={`Odebrat ${row.item.title || row.item.id}`}
      className="shrink-0 rounded p-1 hover:bg-neutral-200 focus-visible:ring-2 focus-visible:ring-primary-500"
      onClick={() => remove(row.item.id)}
    >
      <X size={14} />
    </button>
  );

  return (
    <>
      <Dialog
        open
        onOpenChange={(open) => {
          if (!open) close();
        }}
      >
        <DialogContent
          className={multiple ? 'sm:max-w-[960px]' : 'sm:max-w-[800px]'}
          onEscapeKeyDown={(event) => {
            if (detail || discard || submitting) event.preventDefault();
          }}
        >
          <DialogHeader className="relative pr-12">
            <DialogTitle>{multiple ? 'Vyberte položky' : 'Vyberte položku'}</DialogTitle>
            <DialogDescription>
              {multiple
                ? 'Výběr se drží napříč skupinami'
                : 'Vyberte skupinu a položku, kterou chcete přidat.'}
            </DialogDescription>
            <button
              type="button"
              aria-label="Zavřít výběr"
              disabled={submitting}
              onClick={close}
              className="absolute right-4 top-4 rounded p-1 focus-visible:ring-2"
            >
              <X size={18} />
            </button>
          </DialogHeader>
          <DialogBody className="p-0 my-0 gap-0">
            <fieldset
              disabled={submitting}
              className="m-0 flex min-w-0 flex-col border-0 p-0 sm:flex-row h-[min(400px,48vh)] min-h-[180px]"
            >
              <div className="h-28 shrink-0 sm:h-full sm:w-[30%] min-w-0 border-b sm:border-b-0">
                <PanelWithClassification
                  makeActive={(next) => {
                    if (busy.current) return;
                    setGroup(next);
                    setSearch('');
                  }}
                  active={group ? String(group.id) : undefined}
                  valueAsObject
                  onSearch={() => {}}
                  addItem={() => {}}
                  url={url}
                  width="100%"
                  selectedCounts={Object.fromEntries(
                    [...grouped].map(([id, value]) => [id, value.rows.length])
                  )}
                />
              </div>
              <div className="flex flex-1 min-w-0 min-h-0 flex-col">
                <div className="p-3 border-b border-neutral-150">
                  <Input
                    aria-label="Vyhledat položky"
                    placeholder="Vyhledat položky"
                    data-testid="pick-value-modal-search-input"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    disabled={!group || submitting}
                    className="h-8"
                  />
                </div>
                {hiddenCount > 0 && (
                  <div className="px-3 py-2 text-xs bg-primary-50 text-primary-800">
                    {hiddenCount} vybraných položek je skryto filtrem.{' '}
                    <button type="button" className="underline" onClick={() => setSearch('')}>
                      Zrušit filtr
                    </button>
                  </div>
                )}
                {multiple && group && !loading && !loadError && (
                  <div className="flex items-center gap-2 border-b border-neutral-150 px-3 py-2 text-xs">
                    <Checkbox
                      aria-label={
                        search
                          ? `Vybrat vše (${available.length} nalezených)`
                          : 'Vybrat vše ve skupině'
                      }
                      checked={allChecked ? true : selectedVisible ? 'indeterminate' : false}
                      disabled={!available.length || submitting}
                      onCheckedChange={toggleAll}
                    />
                    <button
                      type="button"
                      disabled={!available.length || submitting}
                      onClick={toggleAll}
                    >
                      {search
                        ? `Vybrat vše (${available.length} nalezených)`
                        : 'Vybrat vše ve skupině'}
                    </button>
                    <span className="ml-auto whitespace-nowrap">
                      {selectedVisible} z {available.length} vybráno
                    </span>
                  </div>
                )}
                {!group ? (
                  <p className="p-6 text-center">Vyberte skupinu.</p>
                ) : loading ? (
                  <p role="status" className="p-6 text-center">
                    Načítám položky…
                  </p>
                ) : loadError ? (
                  <div role="alert" className="p-4">
                    {loadError}{' '}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setRetry((value) => value + 1)}
                    >
                      Zkusit znovu
                    </Button>
                  </div>
                ) : !visibleItems.length ? (
                  <p className="p-6 text-center">
                    {search
                      ? 'Žádné položky neodpovídají hledání.'
                      : 'Ve skupině nejsou dostupné položky.'}
                  </p>
                ) : (
                  <ActionList
                    data={visibleItems}
                    className="h-auto min-h-0 flex-1"
                    showCheckbox={multiple}
                    onSelect={toggle}
                    selected={multiple ? null : current[0]?.item}
                    selectedIds={multiple ? current.map((row) => row.item.id) : undefined}
                    isDisabled={(item) => existing.has(String(item.id))}
                    disabledLabel="Již přidáno"
                    onListKeyDown={(event) => {
                      if (
                        (event.target as HTMLElement).closest('button')?.dataset.pickerCheckbox !==
                        'true'
                      )
                        return;
                      if (
                        multiple &&
                        (event.ctrlKey || event.metaKey) &&
                        event.key.toLowerCase() === 'a'
                      ) {
                        event.preventDefault();
                        selectRows(available);
                      }
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        void submit();
                      }
                    }}
                    actions={
                      showDetail
                        ? (item) => (
                            <Button
                              size="xs"
                              variant="secondary"
                              data-testid={`pick-value-modal-detail-btn-${item.id}`}
                              onClick={() => setDetail(item)}
                            >
                              Detail
                            </Button>
                          )
                        : undefined
                    }
                  />
                )}
              </div>
            </fieldset>
            {multiple && (
              <section
                aria-label="Vybrané položky"
                className="shrink-0 border-t border-neutral-200 bg-neutral-25 p-3 sm:px-5"
              >
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="font-medium text-neutral-800" aria-live="polite">
                    Vybráno{' '}
                    <span className="rounded-full bg-primary-100 px-2 py-0.5 text-primary-700">
                      {current.length}
                    </span>
                  </span>
                  {!!grouped.size && <span>Počet skupin: {grouped.size}</span>}
                  <div className="ml-auto flex gap-3">
                    {!!current.length && (
                      <>
                        <button
                          type="button"
                          disabled={submitting}
                          aria-expanded={expanded}
                          onClick={() => setExpanded(!expanded)}
                          className="text-primary-700"
                        >
                          {expanded ? 'Skrýt seznam' : 'Zobrazit vše'}
                        </button>
                        <button
                          type="button"
                          disabled={submitting}
                          onClick={() => {
                            setUndo(current);
                            setSelection([]);
                            setSubmitError('');
                          }}
                        >
                          Zrušit vše
                        </button>
                      </>
                    )}
                    {undo && (
                      <button
                        type="button"
                        disabled={submitting}
                        onClick={() => {
                          setSelection(undo);
                          setUndo(null);
                        }}
                      >
                        Vrátit zpět
                      </button>
                    )}
                  </div>
                </div>
                {!current.length ? (
                  <p className="mt-2 text-xs">
                    Zaškrtnuté položky se sem ukládají — i z jiných skupin.
                  </p>
                ) : expanded ? (
                  <div className="mt-3 max-h-[22vh] overflow-y-auto space-y-3">
                    {[...grouped.values()].map(({ group: selectedGroup, rows }) => (
                      <div key={selectedGroup.id}>
                        <div className="flex items-center gap-2 text-xs font-medium text-neutral-700">
                          <span className="min-w-0 break-words">{selectedGroup.title}</span>
                          <span>{rows.length}</span>
                          <button
                            type="button"
                            disabled={submitting}
                            className="ml-auto shrink-0 text-primary-700"
                            onClick={() =>
                              change(current.filter((row) => row.group.id !== selectedGroup.id))
                            }
                          >
                            Zrušit skupinu
                          </button>
                        </div>
                        {rows.map((row) => (
                          <div
                            key={row.item.id}
                            className="flex items-center gap-2 border-b border-neutral-100 py-1 text-xs"
                          >
                            <span className="min-w-0 flex-1 break-words">{row.item.title}</span>
                            {removeButton(row)}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {current.slice(0, 3).map((row) => (
                      <div
                        key={row.item.id}
                        title={`${row.group.title}: ${row.item.title}`}
                        className="flex max-w-full items-center gap-1 rounded border border-neutral-200 bg-white pl-2 text-xs sm:max-w-[260px]"
                      >
                        <span className="max-w-16 truncate text-neutral-400">
                          {row.group.title}
                        </span>
                        <span className="min-w-0 truncate">{row.item.title}</span>
                        {removeButton(row)}
                      </div>
                    ))}
                    {current.length > 3 && (
                      <button
                        type="button"
                        disabled={submitting}
                        className="text-xs text-primary-700"
                        onClick={() => setExpanded(true)}
                      >
                        + {current.length - 3} další
                      </button>
                    )}
                  </div>
                )}
              </section>
            )}
            {submitError && (
              <p
                role="alert"
                className="border-t border-red-100 bg-red-50 px-5 py-3 text-sm text-red-700"
              >
                {submitError}
              </p>
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              data-testid="pick-value-modal-close-btn"
              variant="outline"
              onClick={close}
              disabled={submitting}
            >
              Zavřít
            </Button>
            <Button
              data-testid="pick-value-modal-confirm-btn"
              onClick={() => void submit()}
              disabled={!current.length || submitting || !props.onSubmit}
            >
              {submitting ? 'Přidávám…' : multiple ? `Potvrdit (${current.length})` : 'Potvrdit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={!!detail}
        onOpenChange={(open) => {
          if (!open) setDetail(null);
        }}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{detail?.title || 'Detail položky'}</DialogTitle>
            <DialogDescription>Podrobnosti vybrané položky</DialogDescription>
          </DialogHeader>
          <DialogBody>
            <p className="whitespace-pre-wrap">{detail?.description || 'Bez popisu.'}</p>
          </DialogBody>
          <DialogFooter>
            <Button
              data-testid="pick-value-modal-detail-close-btn"
              variant="outline"
              onClick={() => setDetail(null)}
            >
              Zavřít
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={discard} onOpenChange={setDiscard}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Zahodit výběr?</AlertDialogTitle>
            <AlertDialogDescription>
              Vybrané položky ještě nebyly přidány. Zavřením se výběr zruší.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Pokračovat ve výběru</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setDiscard(false);
                onClose?.();
              }}
            >
              Zahodit výběr
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
